import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import _startCase from 'lodash/startCase';
import _toLower from 'lodash/toLower';

import FormStepper from '../../components/FormStepper';
import ControlledForm from '../../components/ControlledForm';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import { useSafetyEventReportData } from '../../core/context/SafetyEventReportDataContext';

import { ReportType, SafetyEventReport } from '../../core/api/clients';

import { getColor } from '../../core/colors';

import useNavigateWithConfirm from '../../core/hooks/useNavigateWithConfirm';
import { useGlobalFormState } from '../../core/context/GlobalFormStateContext';

import SafetyEventFormValidationSchema from './SafetyEventFormValidationSchema';

import EventStep from './FormSteps/EventStep';
import FlightStep from './FormSteps/FlightStep';
import { FormValidationProvider } from '../../core/context/FormValidationContext';
import CrewDebriefStep from './FormSteps/CrewDebriefStep';

function SafetyEventFormEdit(): JSX.Element {
  const authorizedFormData = useAuthorizedFormData();
  const { safetyEventReport, update, updateStatus } = useSafetyEventReportData();
  const { setGlobalFormState } = useGlobalFormState();
  const [submitError, setSubmitError] = React.useState<boolean>(false);
  const [steps] = React.useState<string[]>(['Flight & Crew', 'Event', 'Crew Debrief / Event Review']);
  const navigate = useNavigate();
  const navigateWithConfirm = useNavigateWithConfirm();
  const {
    formState: {
      isDirty,
      dirtyFields,
      ...formStateRest
    },
    ...formMethods
  } = useForm<SafetyEventReport>({
    defaultValues: safetyEventReport,
    mode: 'onBlur',
    resolver: yupResolver(SafetyEventFormValidationSchema),
  });

  React.useEffect(() => {
    formMethods.reset(safetyEventReport);
  }, [safetyEventReport]);

  React.useEffect(() => {
    const { lastModifiedBy } = formMethods.getValues();
    const isSystemUser = lastModifiedBy === 'System';

    setGlobalFormState(isDirty, isSystemUser);
  }, [isDirty, formMethods]);

  const handleSave = () => update(formMethods.getValues());

  const onSubmit = () => {
    setSubmitError(false);
    update(formMethods.getValues(), navigate(`/form/safety-event/${safetyEventReport.id}/review`));
  };

  const onError = () => {
    setSubmitError(true);
  };

  return (
    <ControlledForm
      onSubmit={formMethods.handleSubmit(onSubmit, onError)}
      formProps={{
        noValidate: true,
      }}
    >
      <FormStepper
        control={formMethods.control}
        color={getColor(authorizedFormData.metadata?.color ?? 'grey')}
        steps={steps}
        schema={SafetyEventFormValidationSchema.describe()}
        title="Safety Event Report"
        // eslint-disable-next-line max-len
        reportedBy={`${safetyEventReport.employeeName} [${safetyEventReport.employeeId}], ${_startCase(_toLower(safetyEventReport.employeeTitle ?? ''))}, ${safetyEventReport.employeeLocation}`}
        enableAutoSave={safetyEventReport.lastModifiedBy !== 'System'}
        onSave={handleSave}
        saveStatus={updateStatus}
        lastSaved={safetyEventReport.lastModifiedBy === 'System' ? null : safetyEventReport.lastModifiedOn}
        isDirty={isDirty}
        onClose={() => navigateWithConfirm(authorizedFormData.metadata?.route ?? '')}
        submitError={submitError}
        reportedByVerbage="Created By"
      >
        {({ activeStep }) => (
          <FormProvider formState={{ isDirty, dirtyFields, ...formStateRest }} {...formMethods}>
            <FormValidationProvider<SafetyEventReport>>
              <FlightStep visible={activeStep === 0} />
              <EventStep visible={activeStep === 1} />
              <CrewDebriefStep visible={activeStep === 2} />
            </FormValidationProvider>
          </FormProvider>
        )}
      </FormStepper>
    </ControlledForm>
  );
}

export default SafetyEventFormEdit;
